import { cn } from '@invent/wl-ui-kit-next';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@invent/wl-ui-kit-next/ui';
import { ArrowRight, CornerDownLeft, History, Search, Trash2 } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/design';
import type { IRecordVisit } from '@/design';
import {
  countRecycled,
  findRecords,
  Marked,
  MatchLine,
  SCOPE_LABEL,
  SCOPE_NOUNS,
  scopeListPath,
  searchedScopes,
  searchFailure,
  searchPath,
  seeAllWords,
  TEAM_PATHS,
  type IFoundRecord,
  type ISearchMatch,
  type SearchScope
} from '@/domains/admin';
import { useAuth, useCan, type Can } from '@/domains/auth';
import { liveScreensFor } from './nav';

/**
 * Ctrl/⌘+K: find a person, a team, a firm, a CRD or a screen (the two-portals design, §4.3;
 * SR-414's record search D1 and the owner's Q1).
 *
 * **It runs the reads the lists run, through their own functions, under `q` alone** — People
 * (with `include=roster`, as its page derives from a search: the owner's Q3), Teams, Firms and the
 * SEC ADV register — so each group's five rows are exactly the first page of its list, and its
 * heading's total is that list's "N found". Five rows each, from two characters, a quarter-second
 * after the typing stops, the previous reads cancelled. Nothing is ranked or filtered here: the
 * order is survey-api's, and a screen is matched by its rail label alone. Each row says why it
 * matched — its first reason, as the lists draw it (`MatchLine`); where that reason is the very
 * name the row is drawn by, the name is marked instead and no line repeats it, so a group of five
 * and its "See all" fit in the box's height (`max-h`, what the viewport leaves under the box).
 *
 * **A search shows it is on its way** (the owner's ask of 2026-10-15): from the first keystroke
 * that can search, the groups are drawn, each with two pulsing row shapes until its read lands
 * (`SearchingRows`, not the kit's skeleton, which the suites count across the page), a bar runs
 * along the box's edge while the typing has not settled or a list has not answered
 * (`SearchProgress`, `aria-busy` on the list), and a group's rows fade in one after another as
 * they land. Under reduced motion nothing moves: the shapes and the bar stand still.
 *
 * **Every result is one press away** (Q1). A group that found anything ends with "See all 23
 * people", its list under the same `q`; and the first item of a search is "All results for “q”",
 * highlighted as it is drawn, so Enter opens it unless the reader has moved to a row with the
 * arrows or the pointer — Enter used to open the first person, which the client met as the
 * search choosing for them (SR-386). It opens the search results page (SR-463): every list's
 * matches on one page, by record type.
 *
 * Each group says what its read answered — rows, "no match", or the refusal in survey-api's words
 * (a ninth word is its 400) — so a 403 or an outage in one source is never mistaken for an empty
 * result in another; a 2xx that is no list is a failed read (`searchedPage`), and an answer that
 * never arrived is said as one (`searchFailure`), and the totals line names it.
 * **What is drawn answers the query drawn** (`IFound.q`): a new search shows no row or total of
 * the last one, and Enter pressed before the typing has settled opens the results under what the
 * box holds, never under the last settled query.
 *
 * **A record held only in the recycle bin is not found here** (SR-490 — survey-api leaves it out
 * of the lists): the client wants recycled records found by the bin's own search alone. Where the
 * bin holds responses of records the search reaches, a last item says how many and opens the bin
 * under the same words.
 *
 * **A source is searched only for an account that opens its list** (SR-228): People, Teams and
 * Firms under `responses.browse`, the register under `registry.browse` (`searchedScopes`). A group
 * the account could not follow into is not drawn, as the rail draws no entry for it — and the
 * box's words name only what is searched (`findLabel`, the header's and Team Home's trigger too),
 * so an account searching nothing is offered "Find a screen", not a search that never runs.
 */

type Found =
  | { state: 'loading' }
  | { state: 'done'; rows: IFoundRecord[]; total: number }
  | { state: 'failed'; message: string };

const MIN_QUERY = 2;
const LIMIT = 5;
/** The first item of a search — its cmdk value, which the palette highlights. */
const ALL_RESULTS = 'all-results';

/** The palette's name, and every trigger's: only what this account's search reaches. Each
 *  begins "Find a", which the suites find the triggers by. */
export function findLabel(can: Can): string {
  const scopes = searchedScopes(can);
  if (scopes.includes('people')) return 'Find a person, team, firm, CRD or screen';
  if (scopes.includes('register')) return 'Find a register firm, CRD or screen';
  return 'Find a screen';
}

/** The lists searched, in words — "People, Teams, Firms and the SEC ADV register". */
function searchedWords(scopes: readonly SearchScope[]): string {
  const words = scopes.map((scope) =>
    scope === 'register' ? 'the SEC ADV register' : SCOPE_LABEL[scope]
  );
  const last = words[words.length - 1];
  return words.length > 1 ? `${words.slice(0, -1).join(', ')} and ${last}` : last;
}

/** Each list's five rows under `q`, as the palette draws them. */
async function findIn(scope: SearchScope, q: string, signal: AbortSignal): Promise<Found> {
  const page = await findRecords(scope, { q, pageSize: LIMIT }, signal);
  return { state: 'done', total: page.total, rows: page.items };
}

/** Each list's answer, and the query it answers — drawn only under that query. */
interface IFound {
  q: string;
  answers: Partial<Record<SearchScope, Found>>;
}

export function CommandPalette({
  open,
  onOpenChange,
  recent
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recent: IRecordVisit[];
}) {
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const [query, setQuery] = React.useState('');
  const [settled, setSettled] = React.useState('');
  const [answered, setAnswered] = React.useState<IFound>({ q: '', answers: {} });
  // The item highlighted — cmdk's, held here so a new search starts on "All results".
  const [highlighted, setHighlighted] = React.useState('');
  const scopes = searchedScopes(can);
  const scopeKey = scopes.join(' ');
  const findsAny = scopes.length > 0;
  const label = findLabel(can);
  // SR-490: a record held only in the recycle bin is not in the groups; the bin's own search is
  // offered instead, with how many of its responses this search reaches.
  const binOpen = can('responses.browse');
  const [binned, setBinned] = React.useState<{ q: string; total: number }>({ q: '', total: 0 });

  React.useEffect(() => {
    const timer = setTimeout(() => setSettled(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  React.useEffect(() => {
    if (!open || scopeKey === '') return undefined;
    if (settled.length < MIN_QUERY) {
      // A search typed again after the box went short is asked again, from its row shapes —
      // never drawn first from the answers it had, then taken back for the shapes.
      setAnswered((now) => (now.q === '' ? now : { q: '', answers: {} }));
      return undefined;
    }
    setHighlighted(ALL_RESULTS);
    const controller = new AbortController();
    const asked = scopeKey.split(' ') as SearchScope[];
    setAnswered({ q: settled, answers: {} });
    const land = (scope: SearchScope, answer: Found) => {
      // A read the next search cancelled can still have its body in hand.
      if (controller.signal.aborted) return;
      setAnswered((now) =>
        now.q === settled ? { q: settled, answers: { ...now.answers, [scope]: answer } } : now
      );
    };
    for (const scope of asked) {
      findIn(scope, settled, controller.signal).then(
        (answer) => land(scope, answer),
        (error: unknown) =>
          land(scope, { state: 'failed', message: searchFailure(error, SCOPE_LABEL[scope]) })
      );
    }
    return () => controller.abort();
  }, [open, settled, scopeKey]);

  React.useEffect(() => {
    if (!open || !binOpen || settled.length < MIN_QUERY) return undefined;
    const controller = new AbortController();
    countRecycled(settled, undefined, controller.signal).then(
      (total) => {
        if (!controller.signal.aborted) setBinned({ q: settled, total });
      },
      () => undefined
    );
    return () => controller.abort();
  }, [open, settled, binOpen]);

  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setQuery('');
      setSettled('');
      // Opened again on the same words, the last opening's rows are not this search's answer.
      setAnswered({ q: '', answers: {} });
    }
  };

  const go = (path: string) => {
    close(false);
    navigate(path);
  };

  const typed = query.trim();
  const needle = typed.toLowerCase();
  const screens = user
    ? liveScreensFor(can).filter((entry) => entry.label.toLowerCase().includes(needle))
    : [];
  // The query the groups are drawn for: the settled one — or, before any has settled, what the
  // box holds, so the groups say they are searching from the first keystroke that can search.
  const drawn = settled.length >= MIN_QUERY ? settled : typed;
  const searching = findsAny && drawn.length >= MIN_QUERY;
  // Only the answers to the query drawn — never the last search's, for a frame or for good.
  const found = answered.q === drawn ? answered.answers : {};
  // The box holds a search the typing has not settled on yet.
  const unsettled = typed.length >= MIN_QUERY && typed !== settled;
  // Something is on its way: typing not yet settled, or a list that has not answered it.
  const busy = searching && (unsettled || scopes.some((scope) => found[scope] === undefined));
  const totals = scopes.flatMap((scope) => {
    const answer = found[scope];
    if (answer?.state === 'done') return [`${SCOPE_LABEL[scope]} ${answer.total}`];
    if (answer?.state === 'failed') return [`${SCOPE_LABEL[scope]} could not be searched`];
    return [];
  });
  // Enter before the typing settled: what the box holds — never the last settled query.
  const allResultsPath = searchPath(unsettled ? typed : settled);

  // "All results" is highlighted as it is first drawn — before the typing has settled, too — and
  // again as each search settles (the read's effect above).
  React.useEffect(() => {
    if (searching) setHighlighted(ALL_RESULTS);
  }, [searching]);

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="top-[16%] w-[calc(100%-2rem)] max-w-xl translate-y-0 gap-0 overflow-hidden rounded-lg p-0">
        <DialogTitle className="sr-only">{label}</DialogTitle>
        <DialogDescription className="sr-only">
          {findsAny
            ? `${searchedWords(scopes)} are searched as their own lists search them, five ` +
              'results each. Enter opens all results.'
            : 'Screens are found by their name on the rail.'}
        </DialogDescription>
        <Command
          shouldFilter={false}
          value={highlighted}
          onValueChange={setHighlighted}
          className="rounded-none"
        >
          <CommandInput value={query} onValueChange={setQuery} placeholder={label} />
          <SearchProgress busy={busy} />
          <CommandList
            aria-busy={busy || undefined}
            className="max-h-[min(34rem,calc(84dvh-6.5rem))] py-1"
          >
            {!needle && recent.length > 0 && (
              <CommandGroup heading="Recent">
                {recent.map((record) => (
                  <CommandItem
                    key={record.path}
                    value={`recent:${record.path}`}
                    onSelect={() => go(record.path)}
                  >
                    <History className="text-muted-foreground" aria-hidden />
                    <span className="truncate">{record.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {searching && (
              <CommandGroup>
                <CommandItem
                  value={ALL_RESULTS}
                  data-all-results
                  onSelect={() => go(allResultsPath)}
                >
                  <Search className="text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">All results for “{drawn}”</span>
                    {/* The line is held from the start, so the first total to land moves
                        nothing below it. */}
                    <span className="block truncate text-xs text-muted-foreground">
                      {totals.length > 0 ? (
                        <span className={FADE_IN}>{totals.join(', ')}</span>
                      ) : (
                        <Pulse className="inline-block h-2.5 w-48 align-middle" />
                      )}
                    </span>
                  </span>
                </CommandItem>
              </CommandGroup>
            )}

            {searching &&
              scopes.map((scope) => (
                <ResultGroup
                  key={scope}
                  scope={scope}
                  q={drawn}
                  found={found[scope] ?? { state: 'loading' }}
                  onGo={go}
                />
              ))}

            {searching && binned.q === drawn && binned.total > 0 && (
              <CommandGroup heading="Recycle bin">
                <CommandItem
                  value="recycle-bin"
                  data-recycle-bin
                  onSelect={() =>
                    go(`${TEAM_PATHS.recycleBin}?${new URLSearchParams({ q: drawn }).toString()}`)
                  }
                >
                  <Trash2 className="text-muted-foreground" aria-hidden />
                  <span className="text-[var(--color-sapphire)]">
                    {binned.total === 1
                      ? 'See 1 response in the recycle bin'
                      : `See ${binned.total} responses in the recycle bin`}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}

            {screens.length > 0 && (
              <CommandGroup heading="Screens">
                {screens.map((entry) => {
                  const Icon = entry.icon;
                  return (
                    <CommandItem
                      key={entry.to}
                      value={`screen:${entry.to}`}
                      onSelect={() => go(entry.to)}
                    >
                      <Icon className="text-muted-foreground" aria-hidden />
                      {entry.label}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {findsAny && !searching && needle.length > 0 && screens.length === 0 && (
              <CommandEmpty>
                Type at least two characters to search {searchedWords(scopes)}.
              </CommandEmpty>
            )}

            {!findsAny && needle.length > 0 && screens.length === 0 && (
              <CommandEmpty>
                No screen matches. Your Team Portal roles do not include searching people, teams
                or firms.
              </CommandEmpty>
            )}
          </CommandList>
          <p className="flex items-center gap-1.5 border-t border-border px-3 py-2 text-xs text-muted-foreground">
            <CornerDownLeft className="size-3.5" aria-hidden />
            {searching
              ? 'opens all results; ↑↓ choose one; Esc closes this.'
              : 'opens it; Esc closes this.'}
          </p>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

/** One list's group: its heading with the total, its rows, and its way to the whole list. */
function ResultGroup({
  scope,
  q,
  found,
  onGo
}: {
  scope: SearchScope;
  q: string;
  found: Found;
  onGo: (path: string) => void;
}) {
  const heading = (
    <>
      {SCOPE_LABEL[scope]}
      {found.state === 'done' && <span className={FADE_IN}> · {found.total}</span>}
    </>
  );
  return (
    <CommandGroup heading={heading} data-scope={scope}>
      {found.state === 'loading' && <SearchingRows />}
      {found.state === 'failed' && (
        <p className={cn('px-2 py-2 text-xs text-destructive', FADE_IN)}>{found.message}</p>
      )}
      {found.state === 'done' && found.rows.length === 0 && (
        <p className={cn('px-2 py-2 text-xs text-muted-foreground', FADE_IN)}>
          {scope === 'people' ? 'Nobody matches' : `No ${SCOPE_NOUNS[scope][0]} matches`}
        </p>
      )}
      {found.state === 'done' &&
        found.rows.map((row, index) => {
          const Icon = row.icon;
          const reason = row.matches[0];
          const nameIsReason = reason !== undefined && namesTheRow(reason, row.name);
          return (
            <CommandItem
              key={row.key}
              value={row.key}
              onSelect={() => onGo(row.path)}
              className={FADE_IN}
              style={staggered(index)}
            >
              <Icon className="text-muted-foreground" aria-hidden />
              <div className="min-w-0 flex-1">
                <span data-result-name className="block truncate font-medium">
                  {nameIsReason ? <Marked value={row.name} q={q} rank={reason.rank} /> : row.name}
                </span>
                {row.detail && (
                  <span className="block truncate text-xs text-muted-foreground">
                    {row.detail}
                  </span>
                )}
                {reason && !nameIsReason && (
                  // One reason, cut to a line — the list beside the record says the rest.
                  <div className="text-xs text-muted-foreground [&_p]:truncate">
                    <MatchLine match={reason} q={q} linked={false} />
                  </div>
                )}
              </div>
            </CommandItem>
          );
        })}
      {found.state === 'done' && found.total > 0 && (
        <CommandItem
          value={`see-all:${scope}`}
          data-see-all={scope}
          onSelect={() => onGo(scopeListPath(scope, q))}
          className={FADE_IN}
          style={staggered(found.rows.length)}
        >
          <ArrowRight className="text-muted-foreground" aria-hidden />
          <span className="text-[var(--color-sapphire)]">{seeAllWords(scope, found.total)}</span>
        </CommandItem>
      )}
    </CommandGroup>
  );
}

/** A row landing: faded in, a beat after the one above it (`staggered`); still under reduced
 *  motion. */
const FADE_IN =
  'animate-in fade-in-0 slide-in-from-top-1 fill-mode-backwards duration-200 motion-reduce:animate-none';

/** The n-th row of a group lands a beat after the one above it. */
function staggered(index: number): React.CSSProperties {
  return { '--tw-animation-delay': `${index * 40}ms` } as React.CSSProperties;
}

/** Whether a row's reason is the very name it is drawn by — "Name: Jeffrey Adams" under "Jeffrey
 *  Adams" says nothing the marked name does not. A roster match also names its team or firm, so
 *  it keeps its line. */
function namesTheRow(match: ISearchMatch, name: string): boolean {
  return match.slot === null && match.value.trim().toLowerCase() === name.trim().toLowerCase();
}

/** A shape standing for text on its way: pulsing, and still under reduced motion. */
function Pulse({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('animate-pulse rounded bg-muted motion-reduce:animate-none', className)}
    />
  );
}

/** Two rows' shapes while a list's read is out — not the kit's skeleton, which the suites count
 *  across the page — and "Searching…" for a screen reader. */
function SearchingRows() {
  return (
    <div data-searching className="px-2 py-0.5">
      <span className="sr-only">Searching…</span>
      {[
        ['w-2/5', 'w-3/5'],
        ['w-1/3', 'w-1/2']
      ].map(([name, detail]) => (
        <span key={name} aria-hidden className="flex items-center gap-2 py-1.5">
          <Pulse className="size-4 shrink-0 rounded-full" />
          <span className="grid flex-1 gap-1.5">
            <Pulse className={cn('h-3', name)} />
            <Pulse className={cn('h-2.5', detail)} />
          </span>
        </span>
      ))}
    </div>
  );
}

/** A bar running along the box's lower edge while a search is on its way. Decoration: the list
 *  says `aria-busy`. The strip is always there, so the bar appearing moves nothing. */
function SearchProgress({ busy }: { busy: boolean }) {
  return (
    <div aria-hidden className="relative -mt-px h-0.5 overflow-hidden">
      {busy && (
        <span
          data-search-progress
          className="absolute inset-y-0 left-0 w-1/3 animate-[veritant-splash_1.1s_ease-in-out_infinite] rounded-full bg-[var(--color-sapphire)] motion-reduce:w-full motion-reduce:animate-none motion-reduce:opacity-40"
        />
      )}
    </div>
  );
}
